// src/hooks/usePushNotifications.js
import { useState, useEffect, useCallback } from 'react';
import fetchWithTenant from '../utils/fetchWithTenant';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY;

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if push is supported
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 
                     'PushManager' in window &&
                     'Notification' in window &&
                     VAPID_PUBLIC_KEY;
    setIsSupported(supported);
    
    if (!VAPID_PUBLIC_KEY) {
      console.error('[Push] VAPID_PUBLIC_KEY not configured');
      setLoading(false);
      return;
    }
    
    if (supported) {
      setPermission(Notification.permission);
      checkSubscription();
    } else {
      setLoading(false);
    }
  }, []);

  // Check current subscription status
  const checkSubscription = useCallback(async () => {
    try {
      // Wait for service worker to be ready
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.getSubscription();
      
      setSubscription(sub);
      setIsSubscribed(!!sub);
      setLoading(false);
      // [self-heal] If permission is granted and we have a live subscription,
      // re-post it so the server always holds the current endpoint (iOS rotates
      // these on SW update). Fire-and-forget; never block the UI.
      if (Notification.permission === 'granted' && sub) {
        fetchWithTenant('/api/push/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subscription: sub.toJSON(), userAgent: navigator.userAgent })
        }).catch(() => {});
      }
    } catch (err) {
      console.error('[Push] Error checking subscription:', err);
      setError(err.message);
      setLoading(false);
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch (err) {
      console.error('[Push] Error requesting permission:', err);
      setError(err.message);
      return 'denied';
    }
  }, []);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications not supported');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Request permission if not granted
      let currentPermission = Notification.permission;
      if (currentPermission === 'default') {
        currentPermission = await requestPermission();
      }
      
      if (currentPermission !== 'granted') {
        setError('Notification permission denied');
        setLoading(false);
        return false;
      }

      // Ensure service worker is registered and ready
      let registration;
      try {
        registration = await navigator.serviceWorker.ready;
        console.log('[Push] Service worker ready:', registration.scope);
      } catch (swError) {
        console.error('[Push] Service worker not ready:', swError);
        setError('Service worker not ready. Please refresh the page.');
        setLoading(false);
        return false;
      }

      // Check for existing subscription first
      let sub = await registration.pushManager.getSubscription();
      
      if (sub) {
        console.log('[Push] Found existing subscription, unsubscribing first...');
        await sub.unsubscribe();
      }

      // Convert VAPID key
      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      console.log('[Push] Subscribing with VAPID key length:', applicationServerKey.length);

      // Subscribe to push manager
      try {
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        });
        console.log('[Push] PushManager subscription successful');
      } catch (pushError) {
        console.error('[Push] PushManager.subscribe failed:', pushError);
        
        // Provide more specific error messages
        if (pushError.name === 'AbortError') {
          setError('Push service unavailable. This may be a temporary issue - please try again in a few minutes.');
        } else if (pushError.name === 'NotAllowedError') {
          setError('Push notifications are blocked. Please check your browser settings.');
        } else {
          setError(`Push subscription failed: ${pushError.message}`);
        }
        setLoading(false);
        return false;
      }

      // Send subscription to server using fetchWithTenant
      const response = await fetchWithTenant('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription: sub.toJSON(),
          userAgent: navigator.userAgent
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save subscription on server');
      }

      setSubscription(sub);
      setIsSubscribed(true);
      setLoading(false);
      console.log('[Push] Successfully subscribed');
      return true;

    } catch (err) {
      console.error('[Push] Subscribe error:', err);
      setError(err.message);
      setLoading(false);
      return false;
    }
  }, [isSupported, requestPermission]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    if (!subscription) return true;

    setLoading(true);
    setError(null);

    try {
      // Unsubscribe from push manager
      await subscription.unsubscribe();

      // Remove from server using fetchWithTenant
      await fetchWithTenant('/api/push/unsubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subscription.endpoint
        })
      });

      setSubscription(null);
      setIsSubscribed(false);
      setLoading(false);
      console.log('[Push] Successfully unsubscribed');
      return true;

    } catch (err) {
      console.error('[Push] Unsubscribe error:', err);
      setError(err.message);
      setLoading(false);
      return false;
    }
  }, [subscription]);

  // Send test notification
  const sendTest = useCallback(async () => {
    try {
      const response = await fetchWithTenant('/api/push/test', {
        method: 'POST'
      });
      return response.ok;
    } catch (err) {
      console.error('[Push] Test error:', err);
      return false;
    }
  }, []);

  return {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    requestPermission,
    sendTest
  };
}
